import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const SheetMapper = ({ preview, mapping, setMapping, presetName, setPresetName, onCancel, onImport, busy }) => (
  <div className="mt-6 border border-slate-200 bg-white" data-testid="sheet-mapper">
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
      <div>
        <h3 className="font-display text-base font-bold tracking-tight">
          Match your columns · {preview.filename}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {preview.total_rows} rows found. Keep your own headers — just tell the app what they mean.
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          data-testid="mapping-cancel-button"
          className="rounded-md border-slate-300"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid="mapping-import-button"
          disabled={busy}
          className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
          onClick={onImport}
        >
          Import {preview.total_rows} rows
        </Button>
      </div>
    </div>
    <div className="p-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {preview.fields.map(({ field, required }) => (
          <div key={field}>
            <Label className="text-xs font-bold uppercase tracking-wider">
              {field.replace(/_/g, " ")}
              {required && <span className="text-red-600"> *</span>}
            </Label>
            <Select
              value={mapping[field] || "__none__"}
              onValueChange={(v) => setMapping({ ...mapping, [field]: v === "__none__" ? null : v })}
            >
              <SelectTrigger data-testid={`map-${field}`} className="mt-1 h-10 border-slate-300 bg-white">
                <SelectValue placeholder="Not mapped" />
              </SelectTrigger>
              <SelectContent className="border border-slate-200 bg-white">
                <SelectItem value="__none__">Not mapped</SelectItem>
                {preview.headers.map((h) => (
                  <SelectItem key={h} value={h}>
                    {h}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="mt-6 max-w-md">
        <Label className="text-xs font-bold uppercase tracking-wider">Save this mapping as (optional)</Label>
        <Input
          data-testid="mapping-preset-name-input"
          placeholder="e.g. SharePoint daily sheet"
          className="mt-1 h-10 border-slate-300"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
        />
      </div>

      {preview.presets?.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Apply a saved preset
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preview.presets.map((p) => (
              <Button
                key={p.id}
                size="sm"
                variant="outline"
                data-testid={`apply-preset-${p.id}`}
                className="rounded-md border-slate-300"
                onClick={() => setMapping({ ...mapping, ...p.mapping })}
              >
                {p.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 overflow-x-auto border border-slate-200">
        <table className="w-full text-xs" data-testid="mapping-sample-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left uppercase tracking-wider text-slate-500">
              {preview.headers.map((h) => (
                <th key={h} className="whitespace-nowrap px-3 py-2">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {preview.sample.map((row, i) => (
              <tr key={i}>
                {preview.headers.map((h) => (
                  <td key={h} className="whitespace-nowrap px-3 py-2 text-slate-600">
                    {String(row[h] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);
